// client/src/services/marketingService.js
// API service layer for the Marketing Campaign admin system.
// All methods require admin authentication (handled by api.js interceptor).
//
// Headers and footers are no longer separate parts — body and reward
// templates are now whole emails (header markup, unsubscribe link,
// and footer all baked into a single HTML template). This keeps the
// system consistent with the gift email template pattern.

import api from './api.js';

const BASE = '/admin/marketing';

const marketingService = {

  // ─── Dashboard Stats ──────────────────────────────────────

  getStats: () => api.get(`${BASE}/stats`),


  // ─── Sales Lead Sources ───────────────────────────────────

  getLeadSources: () => api.get(`${BASE}/lead-sources`),

  createLeadSource: (data) => api.post(`${BASE}/lead-sources`, data),

  updateLeadSource: (id, data) => api.put(`${BASE}/lead-sources/${id}`, data),

  deleteLeadSource: (id) => api.delete(`${BASE}/lead-sources/${id}`),


  // ─── Lead Industry Types ──────────────────────────────────

  getLeadIndustries: () => api.get(`${BASE}/lead-industries`),

  createLeadIndustry: (data) => api.post(`${BASE}/lead-industries`, data),

  updateLeadIndustry: (id, data) => api.put(`${BASE}/lead-industries/${id}`, data),

  deleteLeadIndustry: (id) => api.delete(`${BASE}/lead-industries/${id}`),


  // ─── Sales Leads ──────────────────────────────────────────

  getLeads: (params = {}) => {
    const query = new URLSearchParams();
    if (params.page) query.set('page', params.page);
    if (params.limit) query.set('limit', params.limit);
    if (params.search) query.set('search', params.search);
    if (params.sourceid) query.set('sourceid', params.sourceid);
    if (params.industryid) query.set('industryid', params.industryid);
    return api.get(`${BASE}/leads?${query.toString()}`);
  },

  createLead: (data) => api.post(`${BASE}/leads`, data),

  updateLead: (id, data) => api.put(`${BASE}/leads/${id}`, data),

  deleteLead: (id) => api.delete(`${BASE}/leads/${id}`),


  // ─── Reward Templates (whole emails for reward campaigns) ─

  getRewardTemplates: (rewardType) => {
    const query = rewardType ? `?reward_type=${rewardType}` : '';
    return api.get(`${BASE}/reward-templates${query}`);
  },

  createRewardTemplate: (data) => api.post(`${BASE}/reward-templates`, data),

  updateRewardTemplate: (id, data) => api.put(`${BASE}/reward-templates/${id}`, data),

  deleteRewardTemplate: (id) => api.delete(`${BASE}/reward-templates/${id}`),


  // ─── Body Templates (whole emails for informational campaigns) ─

  getBodyTemplates: () => api.get(`${BASE}/body-templates`),

  createBodyTemplate: (data) => api.post(`${BASE}/body-templates`, data),

  updateBodyTemplate: (id, data) => api.put(`${BASE}/body-templates/${id}`, data),

  deleteBodyTemplate: (id) => api.delete(`${BASE}/body-templates/${id}`),


  // ─── Special Access ───────────────────────────────────────

  getSpecialAccess: (params = {}) => {
    const query = new URLSearchParams();
    if (params.page) query.set('page', params.page);
    if (params.limit) query.set('limit', params.limit);
    if (params.userid) query.set('userid', params.userid);
    if (params.campaignid) query.set('campaignid', params.campaignid);
    return api.get(`${BASE}/special-access?${query.toString()}`);
  },

  grantSpecialAccess: (data) => api.post(`${BASE}/special-access`, data),

  deleteSpecialAccess: (id) => api.delete(`${BASE}/special-access/${id}`),


  // ─── Campaigns ────────────────────────────────────────────

  getCampaigns: (params = {}) => {
    const query = new URLSearchParams();
    if (params.page) query.set('page', params.page);
    if (params.limit) query.set('limit', params.limit);
    if (params.status) query.set('status', params.status);
    return api.get(`${BASE}/campaigns?${query.toString()}`);
  },

  getCampaign: (id) => api.get(`${BASE}/campaigns/${id}`),

  createCampaign: (data) => api.post(`${BASE}/campaigns`, data),

  updateCampaign: (id, data) => api.put(`${BASE}/campaigns/${id}`, data),

  deleteCampaign: (id) => api.delete(`${BASE}/campaigns/${id}`),

  // Status actions
  cancelCampaign: (id) => api.post(`${BASE}/campaigns/${id}/cancel`),

  pauseCampaign: (id) => api.post(`${BASE}/campaigns/${id}/pause`),

  resumeCampaign: (id) => api.post(`${BASE}/campaigns/${id}/resume`),

  executeCampaign: (id) => api.post(`${BASE}/campaigns/${id}/execute`),


  // ─── Audience ─────────────────────────────────────────────

  getAudienceColumns: () => api.get(`${BASE}/audience-columns`),

  previewAudience: (data) => api.post(`${BASE}/preview-audience`, data),

  previewCampaignAudience: (id) => api.post(`${BASE}/campaigns/${id}/preview-audience`),


  // ─── Manual Recipient Picker ──────────────────────────────
  // Used by the Audience step's checkbox picker to let admins
  // hand-select specific users / sales leads in addition to (or
  // instead of) the conditions builder. Search runs across email
  // + firstname + lastname.

  // Paginated user list for the picker. Defaults limit to 50.
  getUsersForPicker: (params = {}) => {
    const query = new URLSearchParams();
    query.set('page', params.page || 1);
    query.set('limit', params.limit || 50);
    if (params.search) query.set('search', params.search);
    return api.get(`${BASE}/users-picker?${query.toString()}`);
  },

  // Paginated sales-lead list for the picker.
  getLeadsForPicker: (params = {}) => {
    const query = new URLSearchParams();
    query.set('page', params.page || 1);
    query.set('limit', params.limit || 50);
    if (params.search) query.set('search', params.search);
    return api.get(`${BASE}/leads-picker?${query.toString()}`);
  },

  // Bulk-fetch identity rows for already-picked IDs. Used when an
  // existing campaign is reopened for edit so the chip list can show
  // real names/emails instead of bare ID numbers.
  // payload: { userids: number[], salesleadids: number[] }
  // returns: { users: [...], leads: [...] }
  lookupPickedRecipients: (payload = {}) =>
    api.post(`${BASE}/picker-lookup`, {
      userids: Array.isArray(payload.userids) ? payload.userids : [],
      salesleadids: Array.isArray(payload.salesleadids) ? payload.salesleadids : [],
    }),


  // ─── Campaign Recipients ──────────────────────────────────

  getCampaignRecipients: (campaignId, params = {}) => {
    const query = new URLSearchParams();
    if (params.page) query.set('page', params.page);
    if (params.limit) query.set('limit', params.limit);
    if (params.status) query.set('status', params.status);
    if (params.emailid) query.set('emailid', params.emailid);
    return api.get(`${BASE}/campaigns/${campaignId}/recipients?${query.toString()}`);
  },


  // ─── Campaign Click Analytics ─────────────────────────────

  getCampaignClicks: (campaignId) => api.get(`${BASE}/campaigns/${campaignId}/clicks`),


  // ─── Lookups (for campaign editor dropdowns) ──────────────

  getAvailableTemplates: () => api.get(`${BASE}/available-templates`),

  getAvailablePlans: () => api.get(`${BASE}/available-plans`),

  // Sending domains (uses admin endpoint since marketing has no own)
  getSendingDomains: () => api.get('/admin/sending-domains-active'),

  // Reply emails for campaign reply-to selector
  getReplyEmails: () => api.get('/admin/reply-emails-active'),

};

export default marketingService;